import React, { useState, useEffect, useCallback } from 'react';
import { searchAPI } from '../lib/api';
import DomainCard from '../components/DomainCard';
import { Search, SlidersHorizontal, X, Loader2, BarChart3 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = ['all', 'tech', 'business', 'crypto', 'finance', 'health', 'media', 'gaming', 'ai', 'premium'];
const TLDS = ['all', '.com', '.io', '.ai', '.net', '.org', '.co', '.dev', '.app', '.biz', '.vip'];
const SORTS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'name_asc', label: 'Name: A-Z' },
  { value: 'name_desc', label: 'Name: Z-A' },
  { value: 'most_bids', label: 'Most Bids' },
  { value: 'ending_soon', label: 'Ending Soon' },
  { value: 'valuation_desc', label: 'Highest Valuation' },
];

export default function AdvancedSearchPage() {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: '', category: 'all', tld: 'all', min_price: '', max_price: '',
    has_numbers: 'any', has_hyphens: 'any', sort: 'newest', is_auction: 'all',
    min_valuation: '', date_from: '', date_to: '',
  });

  const doSearch = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => {
        // Convert 'all'/'any' back to empty string for API
        if (v !== '' && v !== 'all' && v !== 'any') params[k] = v;
      });
      const res = await searchAPI.advanced(params);
      setResults(res.data.domains || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch { setResults([]); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { doSearch(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => { e.preventDefault(); doSearch(1); };
  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clearFilters = () => {
    setFilters({ q: '', category: 'all', tld: 'all', min_price: '', max_price: '', has_numbers: 'any', has_hyphens: 'any', sort: 'newest', is_auction: 'all', min_valuation: '', date_from: '', date_to: '' });
  };

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="advanced-search-page">
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-2">
          <Search className="h-6 w-6 text-[#00E5FF]" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">Advanced Search</h1>
        </div>
        <p className="text-zinc-500 mt-2">Find the perfect domain with powerful filters</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
          <Input value={filters.q} onChange={e => updateFilter('q', e.target.value)} placeholder="Search domains by name, keyword..."
            className="pl-10 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="adv-search-input" />
        </div>
        <Button type="submit" className="h-11 px-6 bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="adv-search-btn">Search</Button>
      </form>

      {/* Filters Grid */}
      <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm mb-8" data-testid="adv-filter-panel">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {/* Price Range */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Min Price ($)</Label>
            <Input type="number" value={filters.min_price} onChange={e => updateFilter('min_price', e.target.value)}
              placeholder="0" className="mt-1 h-9 bg-[#050505] border-zinc-800 text-white rounded-sm text-sm" data-testid="filter-min-price" />
          </div>
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Max Price ($)</Label>
            <Input type="number" value={filters.max_price} onChange={e => updateFilter('max_price', e.target.value)}
              placeholder="999999" className="mt-1 h-9 bg-[#050505] border-zinc-800 text-white rounded-sm text-sm" data-testid="filter-max-price" />
          </div>
          {/* Category */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Category</Label>
            <Select value={filters.category} onValueChange={v => updateFilter('category', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-category">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-zinc-300 text-sm">{c === 'all' ? 'All Categories' : c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Extension */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Extension</Label>
            <Select value={filters.tld} onValueChange={v => updateFilter('tld', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-extension">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {TLDS.map(t => <SelectItem key={t} value={t} className="text-zinc-300 text-sm">{t === 'all' ? 'All Extensions' : t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Type */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Type</Label>
            <Select value={filters.is_auction} onValueChange={v => updateFilter('is_auction', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-type">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all" className="text-zinc-300 text-sm">All Types</SelectItem>
                <SelectItem value="true" className="text-zinc-300 text-sm">Auctions Only</SelectItem>
                <SelectItem value="false" className="text-zinc-300 text-sm">Buy Now Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Sort */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Sort By</Label>
            <Select value={filters.sort} onValueChange={v => updateFilter('sort', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {SORTS.map(s => <SelectItem key={s.value} value={s.value} className="text-zinc-300 text-sm">{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Numbers */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Contains Numbers</Label>
            <Select value={filters.has_numbers} onValueChange={v => updateFilter('has_numbers', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-numbers">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="any" className="text-zinc-300 text-sm">Any</SelectItem>
                <SelectItem value="true" className="text-zinc-300 text-sm">Yes</SelectItem>
                <SelectItem value="false" className="text-zinc-300 text-sm">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Hyphens */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Contains Hyphens</Label>
            <Select value={filters.has_hyphens} onValueChange={v => updateFilter('has_hyphens', v)}>
              <SelectTrigger className="mt-1 h-9 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm text-sm" data-testid="filter-hyphens">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="any" className="text-zinc-300 text-sm">Any</SelectItem>
                <SelectItem value="true" className="text-zinc-300 text-sm">Yes</SelectItem>
                <SelectItem value="false" className="text-zinc-300 text-sm">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Min Valuation */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Min Valuation Score</Label>
            <Input type="number" value={filters.min_valuation} onChange={e => updateFilter('min_valuation', e.target.value)}
              placeholder="0" className="mt-1 h-9 bg-[#050505] border-zinc-800 text-white rounded-sm text-sm" data-testid="filter-valuation" />
          </div>
          {/* Date Range */}
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Listed From</Label>
            <Input type="date" value={filters.date_from} onChange={e => updateFilter('date_from', e.target.value)}
              className="mt-1 h-9 bg-[#050505] border-zinc-800 text-white rounded-sm text-sm" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Listed To</Label>
            <Input type="date" value={filters.date_to} onChange={e => updateFilter('date_to', e.target.value)}
              className="mt-1 h-9 bg-[#050505] border-zinc-800 text-white rounded-sm text-sm" data-testid="filter-date-to" />
          </div>
          {/* Actions */}
          <div className="flex items-end gap-2">
            <Button onClick={() => doSearch(1)} className="h-9 bg-[#00E5FF] text-black font-bold rounded-sm text-sm" data-testid="adv-apply-filters">Apply</Button>
            <Button variant="outline" onClick={() => { clearFilters(); }} className="h-9 border-zinc-800 text-zinc-400 rounded-sm text-sm" data-testid="adv-clear-filters">
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mb-4 text-xs text-zinc-600 uppercase tracking-wider" data-testid="search-results-count">
        {total} result{total !== 1 ? 's' : ''} found
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>
      ) : results.length === 0 ? (
        <div className="text-center py-20">
          <Search className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No domains match your search criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="adv-search-results">
          {results.map(d => <DomainCard key={d.id} domain={d} />)}
        </div>
      )}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-12">
          <Button variant="outline" onClick={() => doSearch(page - 1)} disabled={page <= 1} className="border-zinc-800 text-zinc-400 rounded-sm">Previous</Button>
          <span className="px-4 py-2 text-sm text-zinc-500">Page {page}</span>
          <Button variant="outline" onClick={() => doSearch(page + 1)} disabled={results.length < 20} className="border-zinc-800 text-zinc-400 rounded-sm">Next</Button>
        </div>
      )}
    </div>
  );
}
